import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";

const fmtClock = (sec) => {
  const m = Math.floor(Math.max(0, sec) / 60);
  const s = Math.max(0, sec) % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

function luminance(hex) {
  if (!hex || hex[0] !== "#") return 1;
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) || 0;
  const g = parseInt(h.substring(2, 4), 16) || 0;
  const b = parseInt(h.substring(4, 6), 16) || 0;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export default function Scoreboard() {
  const { gameId } = useParams();
  const [game, setGame] = useState(null);
  const [teamA, setTeamA] = useState(null);
  const [teamB, setTeamB] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [localClock, setLocalClock] = useState(0);
  const tickRef = useRef(null);
  const lastServerSeconds = useRef(0);
  const lastServerAt = useRef(0);

  // Poll game state every 2 seconds; locally tick clock for smoothness
  useEffect(() => {
    let active = true;
    const fetchAll = async () => {
      try {
        const g = (await api.get(`/games/${gameId}`)).data;
        if (!active) return;
        // Re-anchor local clock only on first load, when running state changes, or large drift
        const wasRunning = game?.clock_running;
        const willRun = g.clock_running;
        const expectedLocal = wasRunning
          ? Math.max(0, lastServerSeconds.current - Math.floor((Date.now() - lastServerAt.current) / 1000))
          : lastServerSeconds.current;
        const drift = Math.abs((g.clock_seconds || 0) - expectedLocal);
        const shouldAnchor = !game || wasRunning !== willRun || !willRun || drift > 3;
        if (shouldAnchor) {
          lastServerSeconds.current = g.clock_seconds || 0;
          lastServerAt.current = Date.now();
        }
        setGame(g);
        // First-load fetches
        if (!teamA || teamA.id !== g.team_a_id) {
          const ta = (await api.get(`/teams/${g.team_a_id}`)).data;
          if (active) setTeamA(ta);
        }
        if (!teamB || teamB.id !== g.team_b_id) {
          const tb = (await api.get(`/teams/${g.team_b_id}`)).data;
          if (active) setTeamB(tb);
        }
        if (g.tournament_id && (!tournament || tournament.id !== g.tournament_id)) {
          const t = (await api.get(`/tournaments/${g.tournament_id}`)).data;
          if (active) setTournament(t);
        }
      } catch (_) { /* ignore */ }
    };
    fetchAll();
    const poll = setInterval(fetchAll, 2000);
    return () => { active = false; clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // Local clock tick (only when running)
  useEffect(() => {
    if (!game) return;
    const tick = () => {
      if (!game.clock_running) {
        setLocalClock(game.clock_seconds || 0);
        return;
      }
      const elapsed = Math.floor((Date.now() - lastServerAt.current) / 1000);
      const next = Math.max(0, (lastServerSeconds.current || 0) - elapsed);
      setLocalClock(next);
    };
    tick();
    tickRef.current = setInterval(tick, 250);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [game?.clock_running, game?.clock_seconds]);

  if (!game) {
    return (
      <div className="min-h-screen bg-black text-white grid place-items-center font-heading uppercase tracking-widest">
        Aguardando jogo…
      </div>
    );
  }

  const colorA = game.color_a || teamA?.color || "#FFFFFF";
  const colorB = game.color_b || teamB?.color || "#FFD60A";
  const winA = game.status === "finished" && game.score_a > game.score_b;
  const winB = game.status === "finished" && game.score_b > game.score_a;

  return (
    <div className="min-h-screen w-screen bg-black text-white overflow-hidden flex flex-col font-body">
      {/* Top strip: tournament + status */}
      <div className="px-6 py-3 flex items-center justify-between border-b border-zinc-900 bg-zinc-950">
        <div className="font-heading uppercase tracking-[0.3em] text-orange text-base" data-testid="sb-tournament">
          {tournament?.name || "GPS STATS"}
          {tournament?.season && <span className="text-zinc-500 ml-3">{tournament.season}</span>}
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={game.status} />
          {game.overtime && (
            <span className="px-3 py-1 text-xs font-heading uppercase tracking-[0.3em] bg-orange text-white rounded-sm">
              Overtime
            </span>
          )}
        </div>
      </div>

      {/* Main scoreboard — three columns: Team A | Clock | Team B */}
      <div className="flex-1 grid grid-cols-12 items-stretch">
        <TeamSide
          side="A"
          team={teamA}
          color={colorA}
          score={game.score_a}
          fouls={game.fouls_a}
          possession={game.possession === "A"}
          winner={winA}
        />

        {/* Center clock */}
        <div className="col-span-4 flex flex-col items-center justify-center px-4">
          <div className="text-orange font-heading uppercase tracking-[0.4em] text-xl mb-3">
            {game.overtime ? "OT" : "1ST PERIOD"}
          </div>
          <div
            data-testid="sb-clock"
            className="font-mono tabular font-black leading-none text-white"
            style={{
              fontSize: "clamp(5rem, 10vw, 11rem)",
              textShadow: game.clock_running ? "0 0 50px rgba(220,38,38,0.6)" : "none",
            }}
          >
            {fmtClock(localClock)}
          </div>
          {game.clock_running && (
            <div className="mt-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-orange rounded-full animate-pulse" />
              <span className="font-heading uppercase tracking-[0.3em] text-orange text-base">Live</span>
            </div>
          )}
          {!game.clock_running && game.status !== "finished" && (
            <div className="mt-4 font-heading uppercase tracking-[0.3em] text-zinc-500 text-base">
              Paused
            </div>
          )}
          {game.status === "finished" && (
            <div className="mt-4 font-heading uppercase tracking-[0.3em] text-orange text-2xl">
              Final
            </div>
          )}
        </div>

        <TeamSide
          side="B"
          team={teamB}
          color={colorB}
          score={game.score_b}
          fouls={game.fouls_b}
          possession={game.possession === "B"}
          winner={winB}
        />
      </div>

      {/* Bottom footer */}
      <div className="px-6 py-3 flex items-center justify-between border-t border-zinc-900 bg-zinc-950">
        <div className="font-mono uppercase tracking-[0.3em] text-zinc-500 text-xs">
          GPS STATS · 3×3 · LIVE
        </div>
        <div className="font-mono uppercase tracking-[0.3em] text-zinc-500 text-xs">
          {game.date}{game.time ? ` · ${game.time}` : ""}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    live: { c: "bg-red-600 text-white animate-pulse", t: "Live" },
    overtime: { c: "bg-orange text-white", t: "Overtime" },
    finished: { c: "bg-zinc-800 text-zinc-300 border border-zinc-700", t: "Final" },
    scheduled: { c: "bg-transparent border border-zinc-700 text-zinc-400", t: "Scheduled" },
  };
  const s = map[status] || map.scheduled;
  return (
    <span className={`px-3 py-1 text-xs font-heading uppercase tracking-[0.3em] rounded-sm ${s.c}`}>
      {s.t}
    </span>
  );
}

function TeamSide({ side, team, color, score, fouls, possession, winner }) {
  const textColor = luminance(color) > 0.6 ? "#0A0A0A" : "#FFFFFF";
  return (
    <div
      className="col-span-4 flex flex-col items-center justify-center relative overflow-hidden"
      style={{ background: color, color: textColor }}
      data-testid={`sb-team-${side.toLowerCase()}`}
    >
      {/* Possession indicator stripe */}
      {possession && (
        <div
          className="absolute top-0 left-0 right-0 h-2 animate-pulse"
          style={{ background: textColor }}
        />
      )}

      <div className="flex items-center gap-4 px-6 mt-4">
        {team?.logo_url ? (
          <img
            src={fileUrl(team.logo_url)}
            alt=""
            className="w-24 h-24 rounded-sm object-cover bg-white/10 border-2"
            style={{ borderColor: textColor }}
          />
        ) : (
          <div
            className="w-24 h-24 rounded-sm grid place-items-center border-2 bg-black/10"
            style={{ borderColor: textColor }}
          >
            <span className="font-heading font-black text-6xl leading-none">
              {team?.name?.[0] || side}
            </span>
          </div>
        )}
      </div>

      <div className="font-heading font-black uppercase tracking-tight text-4xl xl:text-5xl text-center px-6 mt-4 line-clamp-2">
        {team?.name || `Team ${side}`}
      </div>

      <div
        className="font-mono tabular font-black leading-none mt-4"
        style={{ fontSize: "clamp(8rem, 18vw, 18rem)" }}
        data-testid={`sb-score-${side.toLowerCase()}`}
      >
        {score}
      </div>

      <div className="mt-6 w-full px-6 flex flex-col items-center">
        <div className="font-heading uppercase tracking-[0.3em] opacity-70" style={{ fontSize: "clamp(1rem, 1.6vw, 1.75rem)" }}>Faltas</div>
        <div className="flex items-center gap-4 md:gap-6 mt-1">
          <div
            className="font-mono tabular font-black leading-none"
            style={{ fontSize: "clamp(4rem, 8vw, 8rem)", color: (fouls || 0) >= 6 ? "#DC2626" : textColor }}
            data-testid={`sb-fouls-${side.toLowerCase()}`}
          >
            {fouls || 0}
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <span
                  key={i}
                  className="rounded-full border-2"
                  style={{
                    width: "clamp(0.75rem, 1.4vw, 1.5rem)",
                    height: "clamp(0.75rem, 1.4vw, 1.5rem)",
                    borderColor: textColor,
                    background: i <= (fouls || 0) ? textColor : "transparent",
                  }}
                />
              ))}
            </div>
            {(fouls || 0) >= 6 && (
              <div className="font-heading font-black uppercase tracking-widest text-red-500" style={{ fontSize: "clamp(0.75rem, 1.1vw, 1.2rem)" }}>
                Bônus 7+
              </div>
            )}
          </div>
        </div>
      </div>

      {possession && (
        <div className="mt-3 px-3 py-1 text-xs font-heading uppercase tracking-[0.3em] rounded-sm bg-black/20" style={{ color: textColor }}>
          ▲ Possession
        </div>
      )}

      {winner && (
        <div className="mt-3 px-4 py-1 text-sm font-heading uppercase tracking-[0.3em] rounded-sm bg-black text-orange font-bold">
          ★ Winner
        </div>
      )}
    </div>
  );
}
